import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  directoryApi,
  type CreatePartyRequest,
  type Party,
  type PartyTypeValue,
  type UpdatePartyRequest,
} from '../client';

/**
 * Stable query-key factory for parties. Account scoping is implicit via
 * the active token. The list cache is keyed by `(organisationId, type)`
 * so the Customers and Suppliers pages don't share a cache slot — both
 * pages can be open in different tabs without invalidating each other.
 */
export const partyKeys = {
  all: ['directory', 'parties'] as const,
  byOrganisation: (organisationId: string, type: PartyTypeValue | null) =>
    ['directory', 'parties', 'org', organisationId, type ?? 'all'] as const,
  byId: (id: string) => ['directory', 'parties', 'id', id] as const,
};

/**
 * Fetch every party in an organisation, optionally filtered by `type`.
 *
 * The BE includes `Both` rows in either `Customer` or `Supplier` filter
 * results — so a party flagged as both shows on both list pages without
 * the FE having to special-case it.
 *
 * Pass `{ enabled: false }` (via `options`) to suppress the call (e.g.
 * during the auth bootstrap before a token exists).
 */
export function useParties(
  organisationId: string | null,
  type: PartyTypeValue | null,
  options?: Omit<UseQueryOptions<Party[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<Party[], Error>({
    queryKey: organisationId
      ? partyKeys.byOrganisation(organisationId, type)
      : partyKeys.all,
    queryFn: () => directoryApi.getParties(organisationId!, type ?? undefined),
    enabled: !!organisationId,
    staleTime: 60 * 1000, // 1 minute — parties change rarely.
    ...options,
  });
}

/**
 * Fetch a single party (basic-info shape — addresses / contacts /
 * documents are loaded by their dedicated hooks). Pass `null` to keep
 * the query disabled (e.g. while the route param is still resolving).
 */
export function useParty(id: string | null) {
  return useQuery<Party, Error>({
    queryKey: id ? partyKeys.byId(id) : partyKeys.all,
    queryFn: () => directoryApi.getParty(id!),
    enabled: !!id,
  });
}

/**
 * Create a party. Invalidates BOTH the type-specific list cache and the
 * `'all'`-typed cache (since a new party shows up in either filter).
 * The mutation returns the new party id; the caller usually navigates
 * to the edit route immediately.
 */
export function useCreateParty() {
  const queryClient = useQueryClient();
  return useMutation<string, Error, CreatePartyRequest>({
    mutationFn: (body) => directoryApi.createParty(body),
    onSuccess: (_id, vars) => {
      // Invalidate every list cache for the org — covers `all`, `Customer`,
      // `Supplier`, and `Both` filter slices in one shot.
      queryClient.invalidateQueries({
        queryKey: ['directory', 'parties', 'org', vars.organisationId],
      });
    },
  });
}

/**
 * Update a party. Invalidates the parent org's list (in case status /
 * type shifted the row) and the single-party cache. Caller passes
 * `organisationId` alongside the body so cache invalidation can target
 * the right org without a round-trip.
 */
export function useUpdateParty() {
  const queryClient = useQueryClient();
  return useMutation<string, Error, { body: UpdatePartyRequest; organisationId: string }>({
    mutationFn: ({ body }) => directoryApi.updateParty(body),
    onSuccess: (_id, { body, organisationId }) => {
      queryClient.invalidateQueries({
        queryKey: ['directory', 'parties', 'org', organisationId],
      });
      queryClient.invalidateQueries({ queryKey: partyKeys.byId(body.partyId) });
    },
  });
}

/**
 * Set (or clear, with `null`) a subcontractor's concurrency ceiling.
 *
 * <p>
 * Optimistic on the org list cache, because this is edited inline on a card in
 * the planning cockpit — waiting for a round-trip before the number moves makes
 * the field feel broken. Rolls back on failure.
 * </p>
 */
export function useSetPartyAssignmentCap() {
  const queryClient = useQueryClient();
  return useMutation<
    string,
    Error,
    { partyId: string; organisationId: string; maxConcurrentAssignments: number | null },
    { previous: [readonly unknown[], Party[]][] }
  >({
    mutationFn: ({ partyId, maxConcurrentAssignments }) =>
      directoryApi.setPartyAssignmentCap(partyId, maxConcurrentAssignments),
    onMutate: async ({ partyId, organisationId, maxConcurrentAssignments }) => {
      const listKey = ['directory', 'parties', 'org', organisationId];
      await queryClient.cancelQueries({ queryKey: listKey });
      // Patch every party-list cache for this org — the Suppliers page and the
      // cockpit hold separate slots keyed by `type`, and both show this party.
      const entries = queryClient.getQueriesData<Party[]>({ queryKey: listKey });
      const previous: [readonly unknown[], Party[]][] = [];
      for (const [key, data] of entries) {
        if (!data) continue;
        previous.push([key, data]);
        queryClient.setQueryData<Party[]>(
          key,
          data.map((p) => (p.id === partyId ? { ...p, maxConcurrentAssignments } : p)),
        );
      }
      return { previous };
    },
    onError: (_err, { organisationId }, context) => {
      for (const [key, data] of context?.previous ?? []) {
        queryClient.setQueryData(key, data);
      }
      queryClient.invalidateQueries({
        queryKey: ['directory', 'parties', 'org', organisationId],
      });
    },
    onSuccess: (_id, { partyId }) => {
      queryClient.invalidateQueries({ queryKey: partyKeys.byId(partyId) });
    },
  });
}

/**
 * Set (or clear, with `null`) a customer's document layouts.
 *
 * <p>
 * Pass the WHOLE merged map — `writeLayoutPrefs` builds it — because the column
 * holds every app's branch and this endpoint replaces it wholesale.
 * </p>
 */
export function useSetPartyDocumentLayouts() {
  const queryClient = useQueryClient();
  return useMutation<
    string,
    Error,
    { partyId: string; organisationId: string; preferredDocumentLayoutsJson: string | null }
  >({
    mutationFn: ({ partyId, preferredDocumentLayoutsJson }) =>
      directoryApi.setPartyDocumentLayouts(partyId, preferredDocumentLayoutsJson),
    onSuccess: (_id, { partyId, organisationId }) => {
      queryClient.invalidateQueries({
        queryKey: ['directory', 'parties', 'org', organisationId],
      });
      queryClient.invalidateQueries({ queryKey: partyKeys.byId(partyId) });
    },
  });
}

/**
 * Set (or clear, with `null` / `[]`) the order reference slots a customer
 * insists on.
 *
 * <p>
 * Not optimistic, unlike the assignment cap: this is edited on a form behind a
 * Save button rather than inline on a card, so there is no field sitting still
 * while a round-trip completes. Invalidating both caches matters because the
 * order screen reads the rule off the PARTY LIST it already holds — leaving a
 * stale list would keep warning about a reference nobody requires any more.
 * </p>
 */
export function useSetPartyRequiredOrderReferences() {
  const queryClient = useQueryClient();
  return useMutation<
    string,
    Error,
    { partyId: string; organisationId: string; slots: number[] | null }
  >({
    mutationFn: ({ partyId, slots }) =>
      directoryApi.setPartyRequiredOrderReferences(partyId, slots),
    onSuccess: (_id, { partyId, organisationId }) => {
      queryClient.invalidateQueries({
        queryKey: ['directory', 'parties', 'org', organisationId],
      });
      queryClient.invalidateQueries({ queryKey: partyKeys.byId(partyId) });
    },
  });
}

/**
 * Soft-delete a party. Caller passes `organisationId` so the right
 * list cache gets invalidated (the deleted party's payload isn't
 * available post-delete, so we can't read it back).
 */
export function useDeleteParty() {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, { id: string; organisationId: string }>({
    mutationFn: ({ id }) => directoryApi.deleteParty(id),
    onSuccess: (_, { organisationId, id }) => {
      queryClient.invalidateQueries({
        queryKey: ['directory', 'parties', 'org', organisationId],
      });
      queryClient.removeQueries({ queryKey: partyKeys.byId(id) });
    },
  });
}
